import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { walletAPI } from '../services/api';
import { getProfilePhotoUrl } from '../config';
import { CreditCard, Clock, Wallet, Search, CheckCircle, Smartphone, Phone, Tag, Loader2, MailOpen, DollarSign, Undo2, X } from 'lucide-react';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';

const AdminWallets = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [tab, setTab] = useState('pending'); // 'pending', 'wallets', 'history'
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Modal pour rejeter avec raison
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Modal pour dépôt/remboursement direct
  const [directModal, setDirectModal] = useState(null);
  const [directAmount, setDirectAmount] = useState('');
  const [directDescription, setDirectDescription] = useState('');

  // Recherche et pagination
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, walletsRes] = await Promise.all([
        walletAPI.adminGetPendingDeposits(),
        walletAPI.adminList()
      ]);
      
      if (pendingRes.data.success) {
        setPendingDeposits(pendingRes.data.requests || []);
      }
      if (walletsRes.data.success) {
        setWallets(walletsRes.data.wallets || []);
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!(await confirm('Confirmer ce dépôt ?'))) return;
    
    setProcessing(requestId);
    try {
      const res = await walletAPI.adminApproveDeposit(requestId);
      if (res.data.success) {
        toast.success('Dépôt approuvé !');
        loadData();
      } else {
        toast.error(res.data.error || 'Erreur');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'approbation');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    
    setProcessing(rejectModal.id);
    try {
      const res = await walletAPI.adminRejectDeposit(rejectModal.id, rejectReason);
      if (res.data.success) {
        toast.success('Demande rejetée');
        setRejectModal(null);
        setRejectReason('');
        loadData();
      } else {
        toast.error(res.data.error || 'Erreur');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du rejet');
    } finally {
      setProcessing(null);
    }
  };

  const handleDirectDeposit = async () => {
    if (!directModal || !directAmount) return;
    
    setProcessing(directModal.user_id);
    try {
      const res = await walletAPI.adminDeposit(
        directModal.user_id,
        parseFloat(directAmount),
        directDescription || 'Dépôt admin'
      );
      if (res.data.success) {
        toast.success('Dépôt effectué !');
        setDirectModal(null);
        setDirectAmount('');
        setDirectDescription('');
        loadData();
      } else {
        toast.error(res.data.error || 'Erreur');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleRefund = async () => {
    if (!directModal || !directAmount) return;
    
    setProcessing(directModal.user_id);
    try {
      const res = await walletAPI.adminRefund(
        directModal.user_id,
        parseFloat(directAmount),
        directDescription || 'Remboursement admin'
      );
      if (res.data.success) {
        toast.success('Remboursement effectué !');
        setDirectModal(null);
        setDirectAmount('');
        setDirectDescription('');
        loadData();
      } else {
        toast.error(res.data.error || 'Erreur');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(parseFloat(amount || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'orange_money': 'Orange Money',
      'mtn_money': 'MTN Money',
      'mobile_money': 'Mobile Money',
      'bank_transfer': 'Virement bancaire'
    };
    return methods[method] || method;
  };

  // Filtrage par recherche
  const filteredWallets = wallets.filter(wallet => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (wallet.user_name && wallet.user_name.toLowerCase().includes(searchLower)) ||
      (wallet.user_email && wallet.user_email.toLowerCase().includes(searchLower))
    );
  });

  const filteredPendingDeposits = pendingDeposits.filter(deposit => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (deposit.user_name && deposit.user_name.toLowerCase().includes(searchLower)) ||
      (deposit.user_email && deposit.user_email.toLowerCase().includes(searchLower))
    );
  });

  // Pagination
  const totalPagesWallets = Math.ceil(filteredWallets.length / itemsPerPage);
  const paginatedWallets = filteredWallets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPagesPending = Math.ceil(filteredPendingDeposits.length / itemsPerPage);
  const paginatedPending = filteredPendingDeposits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderPagination = (totalPages, total) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, total)} sur {total}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            ← Précédent
          </button>
          {(() => {
            const pages = [];
            const showPages = new Set();
            showPages.add(1);
            showPages.add(totalPages);
            for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
              showPages.add(i);
            }
            const sortedPages = [...showPages].sort((a, b) => a - b);
            sortedPages.forEach((page, idx) => {
              if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
              }
              pages.push(
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm ${currentPage === page ? 'bg-indigo-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}
                >
                  {page}
                </button>
              );
            });
            return pages;
          })()}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            Suivant →
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/admin" className="hover:text-indigo-600">Admin</Link>
            <span>/</span>
            <span className="text-gray-800">Portefeuilles</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <CreditCard size={32} className="text-indigo-600" />
            <span>Gestion des Portefeuilles</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Gérez les dépôts et les portefeuilles des utilisateurs
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setTab('pending')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                  tab === 'pending'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Clock size={16} /> Dépôts en attente
                {pendingDeposits.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {pendingDeposits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab('wallets')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                  tab === 'wallets'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Wallet size={16} /> Tous les portefeuilles
              </button>
            </nav>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white rounded-xl shadow-sm mb-6 p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4 inline" />
              </button>
            )}
          </div>
        </div>

        {/* Contenu */}
        {tab === 'pending' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                Demandes de dépôt en attente ({filteredPendingDeposits.length})
              </h2>
            </div>
            
            {filteredPendingDeposits.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <div className="text-5xl mb-4 flex justify-center">{search ? <Search size={48} /> : <CheckCircle size={48} className="text-green-500" />}</div>
                <p className="text-lg">{search ? 'Aucun résultat trouvé' : 'Aucune demande en attente'}</p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {paginatedPending.map(deposit => (
                    <div key={deposit.id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                        <img
                          src={getProfilePhotoUrl(deposit.user_photo)}
                          alt={deposit.user_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-gray-800">{deposit.user_name}</p>
                          <p className="text-sm text-gray-500">{deposit.user_email}</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-2xl font-bold text-indigo-600">
                              {formatAmount(deposit.amount)} XOF
                            </p>
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <Smartphone size={14} /> {getPaymentMethodLabel(deposit.payment_method)}
                            </p>
                            {deposit.payment_details && (
                              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded mt-2">
                                {typeof deposit.payment_details === 'string' 
                                  ? deposit.payment_details 
                                  : (
                                    <>
                                      {deposit.payment_details.phone && <p className="flex items-center gap-1"><Phone size={12} /> {deposit.payment_details.phone}</p>}
                                      {deposit.payment_details.transaction_id && <p className="flex items-center gap-1"><Tag size={12} /> ID: {deposit.payment_details.transaction_id}</p>}
                                    </>
                                  )
                                }
                              </div>
                            )}
                            <p className="text-xs text-gray-400">
                              {formatDate(deposit.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(deposit.id)}
                          disabled={processing === deposit.id}
                          className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
                        >
                          {processing === deposit.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <><CheckCircle size={16} /> Approuver</>
                          )}
                        </button>
                        <button
                          onClick={() => setRejectModal(deposit)}
                          disabled={processing === deposit.id}
                          className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-1"
                        >
                          <span><X className="w-4 h-4 inline" /></span> Rejeter
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
                {renderPagination(totalPagesPending, filteredPendingDeposits.length)}
              </>
            )}
          </div>
        )}

        {tab === 'wallets' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Tous les portefeuilles ({filteredWallets.length})
              </h2>
            </div>
            
            {filteredWallets.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <div className="text-5xl mb-4 flex justify-center">{search ? <Search size={48} /> : <MailOpen size={48} />}</div>
                <p>{search ? 'Aucun résultat trouvé' : 'Aucun portefeuille'}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                        <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Solde</th>
                        <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Total déposé</th>
                        <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Total dépensé</th>
                        <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedWallets.map(wallet => (
                        <tr key={wallet.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={getProfilePhotoUrl(wallet.user_photo)}
                                alt={wallet.user_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div>
                                <p className="font-medium text-gray-800">{wallet.user_name}</p>
                              <p className="text-xs text-gray-500">{wallet.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-semibold ${parseFloat(wallet.balance) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatAmount(wallet.balance)} XOF
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {formatAmount(wallet.total_deposited)} XOF
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {formatAmount(wallet.total_spent)} XOF
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setDirectModal({ ...wallet, action: 'deposit' })}
                              className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition flex items-center gap-1"
                            >
                              + Dépôt
                            </button>
                            <button
                              onClick={() => setDirectModal({ ...wallet, action: 'refund' })}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center gap-1"
                            >
                              <Undo2 size={12} /> Remboursement
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
                {renderPagination(totalPagesWallets, filteredWallets.length)}
              </>
            )}
          </div>
        )}

        {/* Modal Rejet */}
        {rejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800">Rejeter la demande</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600">
                  Rejeter la demande de dépôt de <strong>{rejectModal.user_name}</strong> 
                  pour <strong>{formatAmount(rejectModal.amount)} XOF</strong> ?
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raison du rejet (optionnel)
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
                    placeholder="Ex: Preuve de paiement non valide..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setRejectModal(null); setRejectReason(''); }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-50"
                  >
                    {processing ? 'Traitement...' : 'Rejeter'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Dépôt/Remboursement direct */}
        {directModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  {directModal.action === 'deposit' ? <><DollarSign size={20} className="text-green-600" /> Dépôt direct</> : <><Undo2 size={20} className="text-blue-600" /> Remboursement</>}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={getProfilePhotoUrl(directModal.user_photo)}
                    alt={directModal.user_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-gray-800">{directModal.user_name}</p>
                    <p className="text-xs text-gray-500">Solde actuel: {formatAmount(directModal.balance)} XOF</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant (XOF) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={directAmount}
                    onChange={(e) => setDirectAmount(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: 10000"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={directDescription}
                    onChange={(e) => setDirectDescription(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    placeholder={directModal.action === 'deposit' ? 'Ex: Dépôt en espèces' : 'Ex: Remboursement erreur'}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => { setDirectModal(null); setDirectAmount(''); setDirectDescription(''); }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={directModal.action === 'deposit' ? handleDirectDeposit : handleRefund}
                    disabled={processing || !directAmount}
                    className={`flex-1 px-4 py-3 text-white font-semibold rounded-xl transition disabled:opacity-50 ${
                      directModal.action === 'deposit' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {processing ? 'Traitement...' : 'Confirmer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWallets;
